import { FC } from "react";
import { faAngleRight, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Checkbox,
  Combobox,
  Group,
  Radio,
  Text,
} from "@mantine/core";

import styles from "./FilterTreeSelectItem.module.css";

export interface FilterTreeSelectItemData {
  label: string;
  value: string;
  level: number;
  icon?: IconDefinition;
  count?: number;
  suboptions?: string[]; // [Only applicable for level 1 options] the list of suboptions values
  parent?: string; // [Only applicable for level 2 options] the value of the parent option
}

export interface TreeSelectItemProps {
  label: string;
  value: string;
  level: number;
  icon?: IconDefinition;
  count?: number;
  suboptions?: string[];
  parent?: string;
  dataDict: { [key: string]: FilterTreeSelectItemData };
  selectedOptions: { [option: string]: string[] };
  setSelectedValues: (value: string[]) => void;
  expandedStates: { [option: string]: boolean };
  setExpandedStates: (value: { [option: string]: boolean }) => void;
  withTopLevelRadioSelect?: boolean;
}

export const TreeSelectItem: FC<TreeSelectItemProps> = ({
  label,
  value,
  level,
  icon,
  count,
  suboptions,
  parent,
  dataDict,
  selectedOptions,
  setSelectedValues,
  expandedStates,
  setExpandedStates,
  withTopLevelRadioSelect = false,
}) => {
  // Check if the level 1 option is checked: if it has suboptions,
  // check if all of them are selected, otherwise check if the option itself is selected
  const parentChecked = suboptions
    ? selectedOptions[value]?.length === suboptions.length
    : Boolean(selectedOptions[value]);

  // Check if the level 2 option is checked: check if the option is
  // in the list of selected suboptions of its parent
  const childChecked = Boolean(
    parent && selectedOptions[parent]?.includes(value)
  );

  const checkboxChecked = level === 1 ? parentChecked : childChecked;

  const indeterminate =
    level === 1 &&
    suboptions &&
    selectedOptions[value]?.length > 0 &&
    selectedOptions[value]?.length < suboptions.length;

  const getValuesFromSelectedOptions = (selectedOptions: {
    [x: string]: string[];
  }) => {
    const values: string[] = [];
    Object.keys(selectedOptions).forEach((key) => {
      if (
        (dataDict[key].suboptions?.length || 0) === selectedOptions[key].length
      ) {
        values.push(key);
      } else {
        values.push(...selectedOptions[key]);
        // TODO: cannot just add the key -->
        // values.push(key);
      }
    });
    return values;
  };

  const handleCheckboxChange = (
    level: number,
    parent?: FilterTreeSelectItemData
  ) => {
    let updatedOptions = selectedOptions;
    if (level === 1) {
      if (!checkboxChecked) {
        updatedOptions[value] = suboptions || [];
        if (withTopLevelRadioSelect) {
          updatedOptions = { [value]: updatedOptions[value] };
        }
      } else {
        delete updatedOptions[value];
      }
    } else if (parent) {
      const existingSuboptions = updatedOptions[parent.value] || [];
      if (!checkboxChecked) {
        if (!existingSuboptions.includes(value)) {
          updatedOptions[parent.value] = [...existingSuboptions, value];
        }
        if (withTopLevelRadioSelect) {
          updatedOptions = { [parent.value]: updatedOptions[parent.value] };
        }
      } else {
        updatedOptions[parent.value] = existingSuboptions.filter(
          (v) => v !== value
        );
      }
    }
    setSelectedValues(getValuesFromSelectedOptions(updatedOptions));
  };

  const handleToggleExpand = (value: string) => {
    const updatedExpandedStates = { ...expandedStates };
    updatedExpandedStates[value] = !updatedExpandedStates[value];
    setExpandedStates(updatedExpandedStates);
  };

  // collapse the suboptions if the parent is not expanded
  if (level === 2 && !expandedStates[parent ?? ""]) {
    return <div />;
  }

  return (
    <Combobox.Option
      data-testid={`treeselect-item-${value}`}
      key={value}
      value={value}
      className={styles.option}
      // Override the default Mantine behavior of selecting the item on click
      onMouseDown={() => {}}
      onClick={() => {
        handleCheckboxChange(level, dataDict[parent ?? ""]);
      }}
    >
      <Group wrap="nowrap" justify="space-between">
        <Group gap="xs" wrap="nowrap">
          <Group gap={6} wrap="nowrap" pl={20 * level - 14}>
            {icon && <FontAwesomeIcon icon={icon} color="gray" />}
            {withTopLevelRadioSelect && level === 1 ? (
              <Radio
                data-testid={`treeselect-radio-${value}`}
                checked={checkboxChecked || indeterminate}
              />
            ) : (
              <Checkbox
                data-testid={`treeselect-checkbox-${value}`}
                checked={checkboxChecked}
                indeterminate={indeterminate}
              />
            )}
            {label}
          </Group>
          {suboptions && suboptions.length > 0 && (
            <ActionIcon
              color="blue"
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand(value);
              }}
              data-testid={`treeselect-expand-${value}`}
              size="sm"
            >
              <FontAwesomeIcon
                icon={faAngleRight}
                transform={{ rotate: expandedStates[value] ? 90 : 0 }}
              />
            </ActionIcon>
          )}
        </Group>
        {count && (
          <Text span fz={"sm"} c={"gray.6"} style={{ whiteSpace: "nowrap" }}>
            {count}
          </Text>
        )}
      </Group>
    </Combobox.Option>
  );
};
